import type { AnalysisConfig, Building, MetricKey, Placement, ReferenceSummary, YearMetrics } from '../../core/domain/types';
import { MODELS } from '../../core/domain/types';
import { relativeChange } from '../../core/calc/engine';
import type { ResultEntry } from '../../core/worker/protocol';
import { METRICS, MAIN_METRICS, toneColor } from '../../app/metrics';
import { fmt, fmtPct, fmtSigned } from '../../app/format';

export function BuildingMeta({ building, placement }: { building: Building; placement?: Placement }) {
  return (
    <div className="meta-line">
      {building.address && building.address !== building.label && <span>{building.address}</span>}
      {building.egid && <span>EGID {building.egid}</span>}
      <span>
        {building.lat.toFixed(4)}° N, {building.lon.toFixed(4)}° E
      </span>
      {placement ? <span>{fmt(placement.targetElevation)} m ü. M.</span> : building.elevation && <span>{fmt(building.elevation.value)} m ü. M.</span>}
      {placement && (
        <span>
          {MODELS[placement.model].label}-Zelle {placement.cellLat.toFixed(2)}/{placement.cellLon.toFixed(2)}
        </span>
      )}
      {placement && <span>{placement.timezone}</span>}
    </div>
  );
}

/** Standortangaben als umrandete Metadatentabelle. */
export function BuildingMetaTable({ building, placement, cellElevation }: { building: Building; placement?: Placement; cellElevation: number | null }) {
  const rows: [string, string][] = [];
  if (building.address) rows.push(['Adresse', building.address]);
  if (building.egid) rows.push(['EGID', building.egid]);
  rows.push(['Koordinaten', `${building.lat.toFixed(5)}° N, ${building.lon.toFixed(5)}° E`]);
  if (placement) rows.push(['Standorthöhe', `${fmt(placement.targetElevation)} m ü. M. (${placement.targetElevationSource === 'swissALTI3D' ? 'swissALTI3D' : 'Geländemodell 90 m'})`]);
  else if (building.elevation) rows.push(['Standorthöhe', `${fmt(building.elevation.value)} m ü. M.`]);
  if (placement)
    rows.push(['Rasterzelle', `${MODELS[placement.model].label} ${placement.cellLat.toFixed(2)}° N, ${placement.cellLon.toFixed(2)}° E${cellElevation !== null ? `, ${fmt(cellElevation)} m` : ''}`]);
  rows.push(['Zeitzone', placement?.timezone ?? building.timezone ?? 'wird bestimmt']);
  return (
    <table className="meta-table compact" style={{ marginTop: 0 }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <th scope="row">{k}</th>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function KpiRow({
  result,
  cfg,
  selected,
  onSelect,
  stale,
  metrics = MAIN_METRICS,
}: {
  result?: ResultEntry;
  cfg: AnalysisConfig;
  selected?: MetricKey;
  onSelect?: (k: MetricKey) => void;
  stale?: boolean;
  metrics?: MetricKey[];
}) {
  const lastYear = result?.lastCompleteYear ?? null;
  const last = lastYear !== null ? result!.years.find((y) => y.year === lastYear) : undefined;
  return (
    <div className={`kpis${stale ? ' stale' : ''}`} role="group" aria-label="Hauptkennzahlen">
      {metrics.map((k) => (
        <Kpi key={k} k={k} cfg={cfg} last={last} reference={result?.reference} selected={selected === k} onSelect={onSelect} loading={!result} />
      ))}
    </div>
  );
}

function Kpi({ k, cfg, last, reference, selected, onSelect, loading }: { k: MetricKey; cfg: AnalysisConfig; last?: YearMetrics; reference?: ReferenceSummary; selected: boolean; onSelect?: (k: MetricKey) => void; loading: boolean }) {
  const meta = METRICS[k];
  const v = last ? (last[k] as number) : null;
  const ref = reference && reference.status !== 'ungenuegend' ? reference.means[k] : undefined;
  const rel = v !== null && ref !== undefined ? relativeChange(v, ref) : null;
  const diff = v !== null && ref !== undefined ? v - ref : null;
  // Farbe nach Klimasinn (rot = wärmer, blau = kühler), Pfeil nach Richtung des Werts
  const deltaCls = diff === null || diff === 0 ? '' : diff * meta.warmSense > 0 ? 'delta-up' : 'delta-down';
  const arrow = diff === null || diff === 0 ? '' : diff > 0 ? '▲' : '▼';
  const sense = diff === null || diff === 0 ? '' : diff * meta.warmSense > 0 ? 'wärmer' : 'kühler';
  const Tag = onSelect ? 'button' : 'div';
  return (
    <Tag
      {...(onSelect ? { type: 'button' as const, 'aria-pressed': selected, onClick: () => onSelect(k) } : {})}
      className="kpi"
      style={{ ['--accent' as string]: toneColor(meta.tone) }}
      aria-label={onSelect ? `${meta.label}: ${v === null ? 'noch kein Wert' : `${fmt(v, meta.decimals)} ${meta.unit}`}. Im Diagramm anzeigen.` : undefined}
    >
      <span className="kpi-label">{meta.label}</span>
      <span className="kpi-def">{meta.definition(cfg)}</span>
      {loading || v === null ? (
        <>
          <span className="kpi-value">
            <span className="skeleton" style={{ display: 'inline-block', width: 110, height: 34, verticalAlign: 'middle' }} />
          </span>
          <span className="kpi-ref">{loading ? 'Daten werden geladen …' : 'Noch kein vollständiges Jahr'}</span>
        </>
      ) : (
        <>
          <span className="kpi-value">
            {fmt(v, meta.decimals)}
            <span className="kpi-unit">{meta.unit}</span>
          </span>
          <span className="kpi-period">Jahr {last!.year}</span>
          <span className="kpi-ref">
            {ref !== undefined ? (
              <>
                Mittel {reference!.start}–{reference!.end}: {fmt(ref, meta.refDecimals)}
                {reference!.status === 'teil' ? ' (Teilref.)' : ''}
                <br />
                <span className={`kpi-delta ${deltaCls}`}>
                  <span aria-hidden="true">{arrow}</span> {fmtSigned(diff, meta.refDecimals)} {meta.unit}
                  {rel !== null ? ` · ${fmtPct(rel)}` : ' · relativ nicht definiert'}
                  {sense && <span className="sr-only"> ({sense})</span>}
                </span>
              </>
            ) : (
              <>Keine Referenzkennzahl (weniger als 20 Jahre)</>
            )}
          </span>
        </>
      )}
    </Tag>
  );
}
